// Book size constants for realistic aspect ratios in cover/title image generation
// Based on common publishing industry standards

export interface BookSize {
  key: string;
  label: string;
  ratio: string;
  description: string;
  width: number;  // in mm
  height: number; // in mm
  widthInches: string;
  heightInches: string;
}

export interface BookSizeCategory {
  category: string;
  label: string;
  sizes: BookSize[];
}

export const BOOK_SIZES: BookSizeCategory[] = [
  {
    category: 'kdp-optimized',
    label: 'Amazon KDP Optimized',
    sizes: [
      {
        key: 'kdp-ebook',
        label: 'Ebook (Kindle) Cover (1600 × 2400)',
        ratio: '1600:2400',
        description: 'Optimal for Kindle devices, 300 PPI',
        width: 102, // Approximate width in mm for 6"x9" reference
        height: 152, // Approximate height in mm for 6"x9" reference
        widthInches: '5.33',
        heightInches: '8'
      },
      {
        key: 'kdp-paperback-6x9',
        label: 'Paperback 6″ × 9″ with Bleed (1838 × 2775)',
        ratio: '1838:2775',
        description: 'Print-ready with bleed for KDP, 300 DPI',
        width: 152,
        height: 229,
        widthInches: '6.125',
        heightInches: '9.25'
      },
      {
        key: 'kdp-paperback-5x8',
        label: 'Paperback 5″ × 8″ with Bleed (1538 × 2438)',
        ratio: '1538:2438',
        description: 'Compact print format with bleed, 300 DPI',
        width: 127,
        height: 203,
        widthInches: '5.125',
        heightInches: '8.25'
      }
    ]
  },
  {
    category: 'novels',
    label: 'Novels & Fiction',
    sizes: [
      {
        key: 'mass-market',
        label: 'Mass Market Paperback (4.25″ × 6.87″)',
        ratio: '108:175',
        description: 'Pocket-sized novels, budget-friendly',
        width: 108,
        height: 175,
        widthInches: '4.25',
        heightInches: '6.87'
      },
      {
        key: 'digest',
        label: 'Digest Size (5.5″ × 8.5″)',
        ratio: '140:216',
        description: 'Compact format, easy to hold',
        width: 140,
        height: 216,
        widthInches: '5.5',
        heightInches: '8.5'
      },
      {
        key: 'trade-paperback',
        label: 'Trade Paperback (6″ × 9″)',
        ratio: '152:229',
        description: 'Standard novels, most popular',
        width: 152,
        height: 229,
        widthInches: '6',
        heightInches: '9'
      },
      {
        key: 'a5-intl',
        label: 'A5 International (5.83″ × 8.27″)',
        ratio: '148:210',
        description: 'Popular internationally',
        width: 148,
        height: 210,
        widthInches: '5.83',
        heightInches: '8.27'
      }
    ]
  },
  {
    category: 'children',
    label: 'Children\'s Books',
    sizes: [
      {
        key: 'square-picture',
        label: 'Square Picture Books (8″ × 8″)',
        ratio: '203:203',
        description: 'Toddlers & illustrated books',
        width: 203,
        height: 203,
        widthInches: '8',
        heightInches: '8'
      },
      {
        key: 'large-square',
        label: 'Large Square Format (10″ × 10″)',
        ratio: '254:254',
        description: 'Premium picture books',
        width: 254,
        height: 254,
        widthInches: '10',
        heightInches: '10'
      },
      {
        key: 'standard-picture',
        label: 'Standard Picture Books (8.5″ × 11″)',
        ratio: '216:279',
        description: 'Classic children\'s format',
        width: 216,
        height: 279,
        widthInches: '8.5',
        heightInches: '11'
      },
      {
        key: 'large-picture',
        label: 'Large Picture Books (7.5″ × 10″)',
        ratio: '190:254',
        description: 'Oversized illustrations',
        width: 190,
        height: 254,
        widthInches: '7.5',
        heightInches: '10'
      },
      {
        key: 'early-readers',
        label: 'Early Readers (5″ × 7.75″)',
        ratio: '127:197',
        description: 'Chapter books for kids',
        width: 127,
        height: 197,
        widthInches: '5',
        heightInches: '7.75'
      }
    ]
  },
  {
    category: 'textbooks',
    label: 'Textbooks & Academic',
    sizes: [
      {
        key: 'standard-textbook',
        label: 'Standard Textbooks (7″ × 10″)',
        ratio: '178:254',
        description: 'Most common textbook size',
        width: 178,
        height: 254,
        widthInches: '7',
        heightInches: '10'
      },
      {
        key: 'large-textbook',
        label: 'Large Textbooks (8.5″ × 11″)',
        ratio: '216:279',
        description: 'US Letter size textbooks',
        width: 216,
        height: 279,
        widthInches: '8.5',
        heightInches: '11'
      },
      {
        key: 'a4-intl',
        label: 'A4 International (8.27″ × 11.7″)',
        ratio: '210:297',
        description: 'International standard',
        width: 210,
        height: 297,
        widthInches: '8.27',
        heightInches: '11.7'
      },
      {
        key: 'compact-textbook',
        label: 'Compact Format (6″ × 9″)',
        ratio: '152:229',
        description: 'Portable textbooks',
        width: 152,
        height: 229,
        widthInches: '6',
        heightInches: '9'
      }
    ]
  },
  {
    category: 'specialty',
    label: 'Specialty Formats',
    sizes: [
      {
        key: 'coffee-table',
        label: 'Coffee Table Books (10″ × 10″)',
        ratio: '254:254',
        description: 'Art & photography books',
        width: 254,
        height: 254,
        widthInches: '10',
        heightInches: '10'
      },
      {
        key: 'landscape-art',
        label: 'Landscape Art Books (11″ × 8.5″)',
        ratio: '279:216',
        description: 'Horizontal art books',
        width: 279,
        height: 216,
        widthInches: '11',
        heightInches: '8.5'
      },
      {
        key: 'oversized',
        label: 'Oversized Books (12″ × 12″)',
        ratio: '305:305',
        description: 'Premium art & photo books',
        width: 305,
        height: 305,
        widthInches: '12',
        heightInches: '12'
      },
      {
        key: 'pocket-journal',
        label: 'Pocket Journals (4″ × 6″)',
        ratio: '102:152',
        description: 'Small notebooks & journals',
        width: 102,
        height: 152,
        widthInches: '4',
        heightInches: '6'
      },
      {
        key: 'poetry-chapbook',
        label: 'Poetry Chapbooks (5″ × 8″)',
        ratio: '127:203',
        description: 'Poetry & literary works',
        width: 127,
        height: 203,
        widthInches: '5',
        heightInches: '8'
      }
    ]
  }
];

// Helper function to get all sizes as flat array
export const getAllBookSizes = (): BookSize[] => {
  return BOOK_SIZES.flatMap(category => category.sizes);
};

// Helper function to find size by key
export const getBookSizeByKey = (key: string): BookSize | undefined => {
  return getAllBookSizes().find(size => size.key === key);
};

// Helper function to get default book size (most common)
export const getDefaultBookSize = (): BookSize => {
  return getBookSizeByKey('trade-paperback') || getAllBookSizes()[0];
};

// Convert ratio string to aspect ratio number (width/height)
export const getRatioAsNumber = (ratio: string): number => {
  const [width, height] = ratio.split(':').map(Number);
  return width / height;
};